import type {
  Firestore,
  GeoPoint,
  Timestamp
} from 'firebase-admin/firestore'
import { timestamp, auth } from '../../core/database/firebase.js'
import type { FirestoreDoc } from '../../common/types/firestore.js'
import {
  userPreferencesSchema,
  preferencesUpdateSchema,
  profileUpdateSchema,
  preferenceInteractionSchema
} from '@my-roadtrip/validators'
import type {
  ActiveSession,
  AdminUserDetail,
  AdminUserPlanInfo,
  AdminUserQuota,
  CategoryBreakdown,
  CategoryRecommendation,
  MonthlyActivityPoint,
  User,
  UserBehaviorAnalysis,
  UserPreferences,
  UserIconSet,
  LoginHistoryEntry,
  UserProfileResponse,
  UserProfileStats,
  UserRole,
  UserStatus,
  ProfileIconColor,
  SubscriptionSummary
} from '@my-roadtrip/types'
import { calculateDistance } from '@my-roadtrip/utils/geo'
import { randomUUID, createHash } from 'node:crypto'
import { AdminRepository } from '../admin/admin.repository.js'
import { createLogger } from '../../common/utils/logger.js'
import { AIRepository } from '../ai/ai.repository.js'
import { buildAIQuotaSnapshot, normalizePlanTier as normalizePlanTierFromAI, type AIQuotaSnapshot } from '../ai/ai.quota.js'
import { BillingService } from '../billing/billing.service.js'
import { BillingRepository } from '../billing/billing.repository.js'
import { UsersRepository } from '../users/users.repository.js'
import { NotFoundError } from '../../common/errors/AppError.js'
import { CACHE_TTL } from '../../common/constants.js'
import {
  toDateSafe,
  toNumber,
  sanitizeDisplayName as sanitizeDisplayNameUtil,
  sanitizeStringArray,
  normalizeUserStatus as normalizeUserStatusUtil,
  extractCoordinates,
  coerceNumber
} from '../../common/utils/normalize.js'
import { TTLCache } from '../../common/utils/cache.js'
import { isValidUserRole, isString, isNonEmptyString } from '../../common/utils/type-guards.js'

const logger = createLogger('ProfileService')

interface StageLocation {
  lat?: number
  lng?: number
  latitude?: number
  longitude?: number
  coordinates?: {
    lat?: number
    lng?: number
    latitude?: number
    longitude?: number
  }
}

interface StageDoc {
  id: string
  order: number
  duration: number
  location?: StageLocation | GeoPoint | null
  category?: string | null
  type?: string | null
}

interface PlanDoc {
  id: string
  name: string
  createdAt?: Timestamp
  startDate?: Timestamp
  tags?: string[]
  stats?: {
    totalDistance?: number
    totalDuration?: number
    uniqueStops?: number
  }
  routeData?: {
    duration?: number // minutes
    distance?: number // meters
  }
}

interface RequestContext {
  userAgent?: string
  ip?: string
}

type PreferenceUpdates = Partial<Omit<UserPreferences, 'userId'>>

type PlanMetricsResult = {
  stats: UserProfileStats
  monthlyActivity: MonthlyActivityPoint[]
  categoryBreakdown: CategoryBreakdown[]
}

// TTL Cache configuration - using centralized constants
const CACHE_MAX_ENTRIES = 256

export class ProfileService {
  private readonly adminRepository = new AdminRepository()
  private readonly aiRepository = new AIRepository()
  private readonly billingService: BillingService
  private readonly planMetricsCache = new TTLCache<PlanMetricsResult>({ ttl: CACHE_TTL.PLAN_METRICS, maxEntries: CACHE_MAX_ENTRIES })
  private readonly behaviorCache = new TTLCache<UserBehaviorAnalysis>({ ttl: CACHE_TTL.USER_BEHAVIOR, maxEntries: CACHE_MAX_ENTRIES })
  private readonly sessionsCache = new TTLCache<ActiveSession[]>({ ttl: CACHE_TTL.SESSIONS, maxEntries: CACHE_MAX_ENTRIES })
  private readonly loginHistoryCache = new TTLCache<LoginHistoryEntry[]>({ ttl: CACHE_TTL.LOGIN_HISTORY, maxEntries: CACHE_MAX_ENTRIES })
  private readonly allowedIconColors = new Set<ProfileIconColor>([
    'blue',
    'purple',
    'pink',
    'green',
    'orange',
    'red',
    'teal',
    'amber',
    'cyan'
  ])

  constructor(private readonly db: Firestore) {
    this.billingService = new BillingService(new BillingRepository(db), new UsersRepository(db))
  }

  private invalidateSessionCaches(userId: string): void {
    this.sessionsCache.delete(userId)
    this.loginHistoryCache.delete(userId)
  }

  private invalidateUserCaches(userId: string): void {
    this.planMetricsCache.delete(userId)
    this.behaviorCache.delete(userId)
    this.invalidateSessionCaches(userId)
  }

  private createDefaultPreferences(userId: string): UserPreferences {
    return {
      userId,
      travel: {
        defaultTransport: 'car',
        defaultPace: 'normal',
        avoidTolls: false,
        avoidHighways: false,
        avoidFerries: false
      },
      display: {
        theme: 'auto',
        language: 'fr',
        units: 'metric',
        mobileNavType: 'standard'
      },
      notifications: {
        push: true,
        email: true,
        emailFrequency: 'weekly',
        types: {
          tripReminders: true,
          socialActivity: true,
          achievements: true,
          planUpdates: true
        }
      },
      privacy: {
        profilePublic: false,
        showAvatar: true,
        showBio: true,
        showLocation: true,
        showStats: true,
        allowPublicTrips: false,
        allowLocationTracking: false,
        allowAnalytics: false,
        shareLiveWithFriends: true,
        shareLiveWithClans: true
      },
      security: {
        twoFactorEnabled: false,
        twoFactorMethod: null
      },
      updatedAt: timestamp.now().toDate()
    }
  }

  private normalizePreferences(userId: string, raw: unknown): UserPreferences {
    const defaults = this.createDefaultPreferences(userId)
    if (!raw || typeof raw !== 'object') {
      return defaults
    }

    const parsed = userPreferencesSchema
      .omit({ userId: true })
      .partial()
      .safeParse({ userId, ...(raw as Record<string, unknown>) })

    if (!parsed.success) {
      return defaults
    }

    const value = raw as Record<string, any>

    return {
      userId,
      travel: {
        ...defaults.travel,
        ...(value.travel)
      },
      display: {
        ...defaults.display,
        ...(value.display)
      },
      notifications: {
        ...defaults.notifications,
        ...(value.notifications),
        types: {
          ...defaults.notifications.types,
          ...(value.notifications?.types)
        }
      },
      privacy: {
        ...defaults.privacy,
        ...(value.privacy)
      },
      security: {
        ...defaults.security,
        ...(value.security)
      },
      updatedAt: value.updatedAt?.toDate?.() ?? undefined
    }
  }

  private sanitizeDisplayName(value: string): string {
    return sanitizeDisplayNameUtil(value)
  }

  private sanitizeLanguages(value: unknown): string[] {
    return sanitizeStringArray(value, { maxLength: 10 })
  }

  private sanitizeInterests(value: unknown): string[] {
    return sanitizeStringArray(value, { maxLength: 24, lowercase: true })
  }

  private toNumberSafe(value: unknown, fallback = 0): number {
    return toNumber(value, fallback)
  }

  private normalizeDate(value: unknown): Date | null {
    return toDateSafe(value)
  }

  private normalizeUserStatus(value: unknown): UserStatus {
    return normalizeUserStatusUtil(value)
  }



  private normalizeRolesValue(rolesValue: unknown, primaryRole: unknown): UserRole[] {
    const roles = new Set<UserRole>()

    if (Array.isArray(rolesValue)) {
      for (const role of rolesValue) {
        if (isString(role)) {
          const normalized = role.trim()
          if (isValidUserRole(normalized)) {
            roles.add(normalized)
          }
        }
      }
    }

    if (isString(primaryRole)) {
      const normalized = primaryRole.trim()
      if (isValidUserRole(normalized)) {
        roles.add(normalized)
      }
    }

    if (roles.size === 0) {
      roles.add('user')
    }

    return Array.from(roles)
  }

  private normalizePlanTier(value: unknown): AdminUserPlanInfo['tier'] {
    return normalizePlanTierFromAI(value)
  }

  private readonly tierRank: Record<AdminUserPlanInfo['tier'], number> = {
    free: 0,
    premium: 1,
    pro: 2
  }

  private mapSubscriptionQuotaToAdmin(quota: SubscriptionSummary['quota'] | null | undefined): AdminUserQuota | null {
    if (!quota) return null
    const usage = quota.usage ?? { plansGenerated: 0, aiCalls: 0 }
    const limits = quota.limits ?? { plansPerMonth: 0, aiCallsPerMonth: 0, aiCallsPerDay: 0, aiRequestsPerDay: 0 }
    const aiCalls = usage.aiCalls ?? 0
    return {
      current: {
        plansGenerated: usage.plansGenerated ?? 0,
        aiCalls,
        aiRequests: aiCalls
      },
      limits: {
        plansPerMonth: limits.plansPerMonth ?? 0,
        aiCallsPerMonth: limits.aiCallsPerMonth ?? limits.aiCallsPerDay ?? limits.aiRequestsPerDay ?? 0,
        aiRequestsPerDay: limits.aiRequestsPerDay ?? limits.aiCallsPerDay ?? limits.aiCallsPerMonth ?? 0
      },
      window: quota.window
    }
  }

  private normalizeIconColor(value: unknown): ProfileIconColor | undefined {
    if (typeof value !== 'string') {
      return undefined
    }
    const normalized = value.trim().toLowerCase()
    if (this.allowedIconColors.has(normalized as ProfileIconColor)) {
      return normalized as ProfileIconColor
    }
    return undefined
  }

  private normalizeIconSet(value: unknown): UserIconSet | undefined {
    if (!value || typeof value !== 'object') {
      return undefined
    }

    const record = value as Record<string, unknown>
    const rawActive = record.activeIcon
    if (typeof rawActive !== 'string' || rawActive.trim().length === 0) {
      return undefined
    }

    const availableRaw = record.availableIcons
    const available = Array.isArray(availableRaw)
      ? availableRaw
        .filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
        .map(icon => icon.trim())
      : undefined

    const accentColor = this.normalizeIconColor(record.accentColor)

    return {
      activeIcon: rawActive.trim() as UserIconSet['activeIcon'],
      accentColor,
      availableIcons: available as UserIconSet['availableIcons']
    }
  }

  private planLabelFromTier(tier: AdminUserPlanInfo['tier']): string {
    switch (tier) {
      case 'premium':
        return 'Premium'
      case 'pro':
        return 'Pro'
      default:
        return 'Free'
    }
  }

  private buildQuotaSnapshot(quota: unknown): AdminUserQuota {
    const raw = (quota as Record<string, unknown>) ?? {}
    const current = (raw.current as Record<string, unknown> | undefined) ?? {}
    const limits = (raw.limits as Record<string, unknown> | undefined) ?? {}

    const aiUsage = this.toNumberSafe(
      (current.aiRequests ?? current.aiCalls) as number | undefined,
      0
    )
    const aiLimit = this.toNumberSafe(
      (limits.aiRequestsPerDay ?? limits.aiCallsPerMonth) as number | undefined,
      0
    )

    return {
      current: {
        plansGenerated: this.toNumberSafe(current.plansGenerated, 0),
        aiRequests: aiUsage,
        aiCalls: aiUsage
      },
      limits: {
        plansPerMonth: this.toNumberSafe(limits.plansPerMonth, 0),
        aiRequestsPerDay: aiLimit,
        aiCallsPerMonth: aiLimit,
        aiCallsPerDay: this.toNumberSafe(limits.aiCallsPerDay, 0)
      },
      window: raw.window && typeof raw.window === 'object'
        ? {
          period: typeof (raw.window as Record<string, unknown>).period === 'string'
            ? (raw.window as Record<string, unknown>).period as string
            : undefined,
          periodStart: typeof (raw.window as Record<string, unknown>).periodStart === 'string'
            ? (raw.window as Record<string, unknown>).periodStart as string
            : undefined,
          periodEnd: typeof (raw.window as Record<string, unknown>).periodEnd === 'string'
            ? (raw.window as Record<string, unknown>).periodEnd as string
            : undefined
        }
        : undefined
    }
  }

  private async buildAiQuota(
    userId: string,
    planTier: AdminUserPlanInfo['tier']
  ): Promise<AIQuotaSnapshot | null> {
    try {
      const stats = await this.aiRepository.getUsageStats(userId)
      return buildAIQuotaSnapshot(planTier, stats, new Date())
    } catch (error) {
      logger.warn('Failed to build AI quota snapshot', { error })
      return null
    }
  }

  private buildAccountSnapshot(
    userId: string,
    data: Record<string, unknown>,
    adminDetail: AdminUserDetail | null,
    resolvedPlanTier?: AdminUserPlanInfo['tier'],
    aiQuota?: AIQuotaSnapshot | null
  ): User {
    const quotaSource = data.quota ?? data.quotas

    if (adminDetail) {
      return {
        id: userId,
        userId, // Deprecated alias
        email: typeof data.email === 'string' ? data.email : null,
        displayName: typeof data.displayName === 'string' ? data.displayName : '',
        photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
        bio: typeof data.bio === 'string' ? data.bio : null,
        location: typeof data.location === 'string' ? data.location : null,
        iconSet: this.normalizeIconSet(data.iconSet),
        status: adminDetail.status,
        roles: adminDetail.roles,
        plan: adminDetail.plan,
        quota: adminDetail.quota,
        subscriptionStatus: adminDetail.subscriptionStatus ?? null,
        preferences: data.preferences as UserPreferences,
        languages: [],
        interests: [],
        createdAt: this.normalizeDate(adminDetail.createdAt) ?? new Date(),
        updatedAt: this.normalizeDate(data.updatedAt) ?? new Date(),
        lastActive: this.normalizeDate(adminDetail.lastActive),
        lastLoginAt:
          this.normalizeDate(data.lastLoginAt) ??
          this.normalizeDate(data.lastActive) ??
          this.normalizeDate(adminDetail.lastActive),
        suspendedUntil: this.normalizeDate(adminDetail.suspendedUntil),
        suspensionReason: adminDetail.suspensionReason ?? null,
        planCount: adminDetail.planCount,
        shareCount: adminDetail.shareCount,
        flagsCount: adminDetail.flagsCount,
        achievementsCount: adminDetail.achievementsCount ?? this.toNumberSafe(data.achievementsCount, 0)
      }
    }

    const planTier = resolvedPlanTier ?? this.normalizePlanTier(data.plan ?? data.planTier)
    const planId = typeof data.planId === 'string' && data.planId.trim().length > 0 ? data.planId.trim() : undefined
    const planName = typeof data.planName === 'string' && data.planName.trim().length > 0
      ? data.planName.trim()
      : this.planLabelFromTier(planTier)

    const plan: AdminUserPlanInfo = {
      id: planId,
      name: planName,
      tier: planTier
    }

    return {
      id: userId,
      userId, // Deprecated alias
      email: typeof data.email === 'string' ? data.email : null,
      displayName: typeof data.displayName === 'string' ? data.displayName : '',
      photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
      bio: typeof data.bio === 'string' ? data.bio : null,
      location: typeof data.location === 'string' ? data.location : null,
      iconSet: this.normalizeIconSet(data.iconSet),
      status: this.normalizeUserStatus(data.status),
      roles: this.normalizeRolesValue(data.roles, data.role),
      plan,
      quota: this.buildQuotaSnapshot(quotaSource),
      subscriptionStatus:
        typeof data.subscriptionStatus === 'string' && data.subscriptionStatus.trim().length > 0
          ? data.subscriptionStatus.trim()
          : null,
      preferences: data.preferences as UserPreferences,
      languages: [],
      interests: [],
      createdAt: this.normalizeDate(data.createdAt) ?? new Date(),
      updatedAt: this.normalizeDate(data.updatedAt) ?? new Date(),
      lastActive: this.normalizeDate(data.lastActive),
      lastLoginAt: this.normalizeDate(data.lastLoginAt),
      suspendedUntil: this.normalizeDate(data.suspendedUntil),
      suspensionReason:
        typeof data.suspensionReason === 'string' && data.suspensionReason.trim().length > 0
          ? data.suspensionReason.trim()
          : null,
      planCount: this.toNumberSafe(data.planCount, 0),
      shareCount: this.toNumberSafe(data.shareCount, 0),
      flagsCount: this.toNumberSafe(data.flagsCount, 0),
      achievementsCount: this.toNumberSafe(data.achievementsCount, 0)
    }
  }

  private parseUserAgent(userAgent?: string): string {
    if (!userAgent) return 'Appareil inconnu'
    const ua = userAgent.toLowerCase()
    if (ua.includes('iphone')) return 'iPhone'
    if (ua.includes('ipad')) return 'iPad'
    if (ua.includes('android') && ua.includes('mobile')) return 'Android (Mobile)'
    if (ua.includes('android')) return 'Android'
    if (ua.includes('windows')) return 'Windows'
    if (ua.includes('mac os') || ua.includes('macintosh')) return 'macOS'
    if (ua.includes('linux')) return 'Linux'
    return 'Appareil inconnu'
  }

  private getSeasonForMonth(monthIndex: number): 'winter' | 'spring' | 'summer' | 'autumn' {
    const normalized = ((monthIndex % 12) + 12) % 12
    if (normalized === 11 || normalized <= 1) return 'winter'
    if (normalized >= 2 && normalized <= 4) return 'spring'
    if (normalized >= 5 && normalized <= 7) return 'summer'
    return 'autumn'
  }

  private formatMonthLabel(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
  }

  private timeOfDayFromHour(hour: number): 'morning' | 'afternoon' | 'evening' | 'night' {
    if (hour >= 5 && hour < 12) return 'morning'
    if (hour >= 12 && hour < 17) return 'afternoon'
    if (hour >= 17 && hour < 22) return 'evening'
    return 'night'
  }


  private mapStageSnapshot(stageDoc: FirestoreDoc): StageDoc {
    const data = stageDoc.data() as Record<string, unknown>
    const location = (data.location as StageLocation | GeoPoint | null) ?? null
    const rawCategory =
      typeof data.category === 'string'
        ? data.category
        : typeof data.type === 'string'
          ? data.type
          : null

    const orderValue = coerceNumber(data.order)
    const durationValue =
      coerceNumber(data.duration) ??
      coerceNumber((data as { durationMinutes?: unknown }).durationMinutes) ??
      0

    return {
      id: stageDoc.id,
      order: orderValue ?? 0,
      duration: durationValue,
      location,
      category: rawCategory,
      type: typeof data.type === 'string' ? data.type : null
    }
  }

  private async fetchStagesForPlans(planDocs: FirestoreDoc[]): Promise<Map<string, StageDoc[]>> {
    const stageMap = new Map<string, StageDoc[]>()
    if (planDocs.length === 0) {
      return stageMap
    }

    const batchSize = 5
    for (let index = 0; index < planDocs.length; index += batchSize) {
      const batch = planDocs.slice(index, index + batchSize)
      const snapshots = await Promise.all(
        batch.map(planDoc =>
          this.db
            .collection('stages')
            .where('planId', '==', planDoc.id)
            .orderBy('order')
            .get()
        )
      )

      snapshots.forEach((snapshot, snapshotIndex) => {
        const planId = batch[snapshotIndex].id
        if (snapshot.empty) {
          stageMap.set(planId, [])
          return
        }

        stageMap.set(
          planId,
          snapshot.docs.map(doc => this.mapStageSnapshot(doc as FirestoreDoc))
        )
      })
    }

    return stageMap
  }

  private async collectPlanMetrics(userId: string): Promise<PlanMetricsResult> {
    const cached = this.planMetricsCache.get(userId)
    if (cached) {
      return cached
    }

    const plansSnapshot = await this.db.collection('plans').where('userId', '==', userId).get()
    const planDocs = plansSnapshot.docs as FirestoreDoc[]

    if (planDocs.length === 0) {
      const empty: PlanMetricsResult = {
        stats: {
          totalTrips: 0,
          totalKm: 0,
          totalHours: 0,
          uniqueStops: 0
        },
        monthlyActivity: [],
        categoryBreakdown: []
      }
      this.planMetricsCache.set(userId, empty)
      return empty
    }

    const monthlyMap = new Map<string, number>()
    const categoryMap = new Map<string, number>()
    let totalDistanceMeters = 0
    let totalDurationMinutes = 0
    const uniqueStops = new Set<string>()

    const stageMap = await this.fetchStagesForPlans(planDocs)

    for (const planDoc of planDocs) {
      const planData = planDoc.data() as PlanDoc
      const createdAt =
        planData.startDate?.toDate?.() ??
        planData.createdAt?.toDate?.() ??
        planDoc.createTime?.toDate?.() ??
        new Date()

      const monthKey = this.formatMonthLabel(createdAt)
      monthlyMap.set(monthKey, (monthlyMap.get(monthKey) ?? 0) + 1)

      if (Array.isArray(planData.tags)) {
        for (const rawTag of planData.tags) {
          if (typeof rawTag === 'string') {
            const tag = rawTag.trim().toLowerCase()
            if (tag.length > 0) {
              categoryMap.set(tag, (categoryMap.get(tag) ?? 0) + 1)
            }
          }
        }
      }

      let hasAggregatedDistance = false
      const aggregatedDistanceMeters = coerceNumber(planData.stats?.totalDistance)
      if (aggregatedDistanceMeters !== null) {
        totalDistanceMeters += aggregatedDistanceMeters
        hasAggregatedDistance = true
      } else {
        const routeDistanceKm = coerceNumber(planData.routeData?.distance)
        if (routeDistanceKm !== null) {
          const normalizedRouteKm = routeDistanceKm > 10000 ? routeDistanceKm / 1000 : routeDistanceKm
          totalDistanceMeters += normalizedRouteKm * 1000
          hasAggregatedDistance = true
        }
      }

      let hasAggregatedDuration = false
      const aggregatedDurationSeconds = coerceNumber(planData.stats?.totalDuration)
      if (aggregatedDurationSeconds !== null) {
        totalDurationMinutes += aggregatedDurationSeconds / 60
        hasAggregatedDuration = true
      } else {
        const routeDurationMinutes = coerceNumber(planData.routeData?.duration)
        if (routeDurationMinutes !== null) {
          totalDurationMinutes += routeDurationMinutes
          hasAggregatedDuration = true
        }
      }

      const stages = stageMap.get(planDoc.id) ?? []

      if (!hasAggregatedDistance && stages.length > 1) {
        for (let i = 0; i < stages.length - 1; i++) {
          const from = extractCoordinates(stages[i].location)
          const to = extractCoordinates(stages[i + 1].location)
          if (from && to) {
            totalDistanceMeters += calculateDistance(from, to)
          }
        }
      }

      for (const stage of stages) {
        uniqueStops.add(stage.id)
        if (!hasAggregatedDuration && stage.duration > 0) {
          totalDurationMinutes += stage.duration
        }
        const categorySource = stage.category ?? stage.type
        if (categorySource) {
          const key = categorySource.trim().toLowerCase()
          if (key.length > 0) {
            categoryMap.set(key, (categoryMap.get(key) ?? 0) + 1)
          }
        }
      }
    }

    const totalTrips = planDocs.length
    const totalKm = Math.round(totalDistanceMeters / 1000)
    const totalHours = Math.round(totalDurationMinutes / 60)
    const stats: UserProfileStats = {
      totalTrips,
      totalKm,
      totalHours,
      uniqueStops: uniqueStops.size
    }

    const monthlyActivity: MonthlyActivityPoint[] = Array.from(monthlyMap.entries())
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([month, trips]) => ({ month, trips }))
      .slice(-12)

    const sortedCategories = Array.from(categoryMap.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 12)

    const categoryBreakdown: CategoryBreakdown[] = sortedCategories.map(([category, value]) => ({
      category,
      value
    }))

    const result: PlanMetricsResult = { stats, monthlyActivity, categoryBreakdown }
    this.planMetricsCache.set(userId, result)

    return result
  }

  private async touchSession(userId: string, context: RequestContext): Promise<void> {
    this.invalidateSessionCaches(userId)
    const userDoc = this.db.collection('users').doc(userId)
    const sessionsCollection = userDoc.collection('sessions')
    const userAgent = context.userAgent ?? 'unknown'
    const ip = context.ip ?? '0.0.0.0'
    const device = this.parseUserAgent(userAgent)

    const hash = createHash('sha1')
      .update(userAgent)
      .update(ip)
      .digest('hex')
      .slice(0, 20)
    const sessionId = hash || randomUUID()
    const sessionRef = sessionsCollection.doc(sessionId)
    const existing = await sessionRef.get()

    const now = timestamp.now()

    if (existing.exists) {
      await sessionRef.set(
        {
          device,
          userAgent,
          ip,
          lastActive: now,
          current: true,
          updatedAt: now
        },
        { merge: true }
      )
    } else {
      const otherSessions = await sessionsCollection.get()
      for (const session of otherSessions.docs) {
        if (session.id !== sessionId) {
          await session.ref.set({ current: false }, { merge: true })
        }
      }

      await sessionRef.set({
        device,
        userAgent,
        ip,
        createdAt: now,
        lastActive: now,
        current: true,
        updatedAt: now
      })

      await userDoc.collection('loginHistory').add({
        timestamp: now,
        device,
        ip,
        location: null,
        success: true
      })
    }
  }

  private async getSessions(userId: string): Promise<ActiveSession[]> {
    const cached = this.sessionsCache.get(userId)
    if (cached) {
      return cached
    }

    const snapshot = await this.db
      .collection('users')
      .doc(userId)
      .collection('sessions')
      .orderBy('lastActive', 'desc')
      .limit(20)
      .get()

    const sessions = snapshot.docs.map((doc: FirestoreDoc) => {
      const data = doc.data() as Record<string, any>
      const device =
        typeof data.device === 'string' && data.device.trim().length > 0 ? data.device : 'Appareil'
      const userAgent =
        typeof data.userAgent === 'string' && data.userAgent.trim().length > 0
          ? data.userAgent
          : undefined
      const location =
        typeof data.location === 'string' && data.location.trim().length > 0 ? data.location : null
      const ip =
        typeof data.ip === 'string' && data.ip.trim().length > 0 ? data.ip : '0.0.0.0'

      return {
        id: doc.id,
        device,
        userAgent,
        location,
        lastActive: data.lastActive?.toDate?.() ?? new Date(),
        ip,
        current: Boolean(data.current),
        createdAt: data.createdAt?.toDate?.() ?? new Date()
      }
    })

    this.sessionsCache.set(userId, sessions)
    return sessions
  }

  private async getLoginHistory(userId: string): Promise<LoginHistoryEntry[]> {
    const cached = this.loginHistoryCache.get(userId)
    if (cached) {
      return cached
    }

    const snapshot = await this.db
      .collection('users')
      .doc(userId)
      .collection('loginHistory')
      .orderBy('timestamp', 'desc')
      .limit(30)
      .get()

    const history = snapshot.docs.map((doc: FirestoreDoc) => {
      const data = doc.data() as Record<string, any>
      const device =
        typeof data.device === 'string' && data.device.trim().length > 0 ? data.device : 'Appareil'
      const ip =
        typeof data.ip === 'string' && data.ip.trim().length > 0 ? data.ip : '0.0.0.0'
      const location =
        typeof data.location === 'string' && data.location.trim().length > 0 ? data.location : null
      const failureReason =
        typeof data.failureReason === 'string' && data.failureReason.trim().length > 0
          ? data.failureReason
          : null

      return {
        id: doc.id,
        timestamp: data.timestamp?.toDate?.() ?? new Date(),
        device,
        ip,
        location,
        success: data.success !== false,
        failureReason
      }
    })

    this.loginHistoryCache.set(userId, history)
    return history
  }

  private async analyzeUserBehavior(userId: string): Promise<UserBehaviorAnalysis> {
    const cached = this.behaviorCache.get(userId)
    if (cached) {
      return cached
    }

    const interactionsSnapshot = await this.db
      .collection('users')
      .doc(userId)
      .collection('preferenceInteractions')
      .orderBy('timestamp', 'desc')
      .limit(200)
      .get()

    if (interactionsSnapshot.empty) {
      const defaultAnalysis: UserBehaviorAnalysis = {
        topCategories: [],
        averageStayMinutes: 0,
        preferredTimeOfDay: 'morning',
        seasonalPreferences: {
          spring: [],
          summer: [],
          autumn: [],
          winter: []
        },
        budgetRange: 'medium',
        companyPreference: 'mixed'
      }
      this.behaviorCache.set(userId, defaultAnalysis)
      return defaultAnalysis
    }

    const categories = new Map<string, number>()
    const durations: number[] = []
    const timeOfDayBuckets = new Map<'morning' | 'afternoon' | 'evening' | 'night', number>()
    const seasonal = {
      spring: new Map<string, number>(),
      summer: new Map<string, number>(),
      autumn: new Map<string, number>(),
      winter: new Map<string, number>()
    }
    const budgetCounts = new Map<string, number>()
    const companyCounts = new Map<string, number>()

    for (const interactionDoc of interactionsSnapshot.docs) {
      const data = interactionDoc.data()
      const category = typeof data.category === 'string' ? data.category.trim().toLowerCase() : null
      const duration = typeof data.duration === 'number' ? data.duration : null
      const budget = typeof data.budget === 'string' ? data.budget : null
      const company = typeof data.company === 'string' ? data.company : null
      const timestampValue = data.timestamp?.toDate?.() ?? interactionDoc.createTime?.toDate?.() ?? new Date()

      if (category) {
        categories.set(category, (categories.get(category) ?? 0) + 1)

        const season = this.getSeasonForMonth(timestampValue.getMonth())
        const seasonMap = seasonal[season]
        seasonMap.set(category, (seasonMap.get(category) ?? 0) + 1)
      }

      if (duration && duration > 0) {
        durations.push(duration)
      }

      const hour = timestampValue.getHours()
      const bucket = this.timeOfDayFromHour(hour)
      timeOfDayBuckets.set(bucket, (timeOfDayBuckets.get(bucket) ?? 0) + 1)

      if (budget) {
        budgetCounts.set(budget, (budgetCounts.get(budget) ?? 0) + 1)
      }

      if (company) {
        companyCounts.set(company, (companyCounts.get(company) ?? 0) + 1)
      }
    }

    const topCategories = Array.from(categories.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([category]) => category)

    const averageStayMinutes =
      durations.length > 0 ? Math.round(durations.reduce((acc, val) => acc + val, 0) / durations.length) : 0

    const preferredTimeOfDay =
      Array.from(timeOfDayBuckets.entries()).sort(([, a], [, b]) => b - a)[0]?.[0] ?? 'morning'

    const seasonalPreferences = {
      spring: Array.from(seasonal.spring.entries())
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([category]) => category),
      summer: Array.from(seasonal.summer.entries())
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([category]) => category),
      autumn: Array.from(seasonal.autumn.entries())
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([category]) => category),
      winter: Array.from(seasonal.winter.entries())
        .sort(([, a], [, b]) => b - a)
        .slice(0, 3)
        .map(([category]) => category)
    }

    const budgetRange =
      Array.from(budgetCounts.entries()).sort(([, a], [, b]) => b - a)[0]?.[0] ?? 'medium'

    const companyPreference =
      Array.from(companyCounts.entries()).sort(([, a], [, b]) => b - a)[0]?.[0] ?? 'mixed'

    const analysis: UserBehaviorAnalysis = {
      topCategories,
      averageStayMinutes,
      preferredTimeOfDay,
      seasonalPreferences,
      budgetRange: (budgetRange as UserBehaviorAnalysis['budgetRange']) ?? 'medium',
      companyPreference: (companyPreference as UserBehaviorAnalysis['companyPreference']) ?? 'mixed'
    }

    this.behaviorCache.set(userId, analysis)
    return analysis
  }

  private buildPreferencePayload(preferences: UserPreferences) {
    return {
      travel: preferences.travel,
      display: preferences.display,
      notifications: preferences.notifications,
      privacy: preferences.privacy,
      security: preferences.security,
      updatedAt: timestamp.now()
    }
  }

  async getProfile(userId: string, context: RequestContext): Promise<UserProfileResponse> {
    logger.info('Fetching user profile', { userId })
    const userRef = this.db.collection('users').doc(userId)
    const userDoc = await userRef.get()

    if (!userDoc.exists) {
      const env = (process.env.NODE_ENV || '').toLowerCase()
      const e2eBypass =
        (process.env.E2E_AUTH_BYPASS === 'true' || process.env.MODE === 'e2e') &&
        env !== 'production' &&
        env !== 'prod'

      if (!e2eBypass) {
        logger.warn('User profile not found', { userId })
        throw new NotFoundError('User profile not found', { userId })
      }

      const defaults = this.createDefaultPreferences(userId)
      const now = timestamp.now()
      const fallbackProfile: Record<string, unknown> = {
        displayName: 'E2E User',
        email: `${userId}@roadtrip.test`,
        photoURL: null,
        bio: null,
        location: null,
        plan: 'premium',
        subscriptionStatus: 'active',
        status: 'active' as UserStatus,
        roles: ['admin', 'user'],
        createdAt: now,
        updatedAt: now,
        lastLoginAt: now,
        lastActive: now,
        preferences: defaults
      }

      await userRef.set(fallbackProfile, { merge: true })
      logger.info('E2E fallback profile created', { userId })
      return this.getProfile(userId, context)
    }

    logger.debug('Fetching profile data', { userId })
    await this.touchSession(userId, context)

    const rawData = (userDoc.data() ?? {}) as Record<string, unknown>

    const [planMetrics, sessions, loginHistory, analysis, adminDetail, billingSummary] = await Promise.all([
      this.collectPlanMetrics(userId),
      this.getSessions(userId),
      this.getLoginHistory(userId),
      this.analyzeUserBehavior(userId),
      this.adminRepository.getUserById(userId),
      this.billingService.getSubscriptionSummary(userId).catch(() => null)
    ])

    const preferences = this.normalizePreferences(userId, rawData.preferences)

    const resolvedPlanTier = this.normalizePlanTier(rawData.plan ?? rawData.planTier)
    const aiQuota = await this.buildAiQuota(userId, resolvedPlanTier)

    const memberSince =
      this.normalizeDate(rawData.createdAt) ??
      userDoc.createTime?.toDate?.() ??
      new Date()

    const accountSnapshot = this.buildAccountSnapshot(userId, rawData, adminDetail, resolvedPlanTier, aiQuota)

    // Overlay billing as single source of truth when available (already fetched in parallel)
    if (billingSummary) {
      accountSnapshot.plan = {
        id: billingSummary.plan.id,
        name: billingSummary.plan.name,
        tier: billingSummary.plan.tier
      }
      const billingQuota = this.mapSubscriptionQuotaToAdmin(billingSummary.quota)
      if (billingQuota) accountSnapshot.quota = billingQuota
      accountSnapshot.subscriptionStatus = billingSummary.subscription?.status ?? null
    }

    const resolvedPhotoURL =
      typeof rawData.photoURL === 'string'
        ? rawData.photoURL
        : typeof rawData.avatarUrl === 'string'
          ? rawData.avatarUrl
          : null

    return {
      id: userId,
      email: typeof rawData.email === 'string' ? rawData.email : null,
      displayName: typeof rawData.displayName === 'string' ? rawData.displayName : '',
      photoURL: resolvedPhotoURL,
      avatarUrl:
        typeof rawData.avatarUrl === 'string'
          ? rawData.avatarUrl
          : resolvedPhotoURL,
      bio: typeof rawData.bio === 'string' ? rawData.bio : null,
      location: typeof rawData.location === 'string' ? rawData.location : null,
      iconSet: this.normalizeIconSet(rawData.iconSet),
      languages: this.sanitizeLanguages(rawData.languages),
      interests: this.sanitizeInterests(rawData.interests),
      memberSince,
      stats: planMetrics.stats,
      preferences,
      analysis,
      sessions,
      loginHistory,
      monthlyActivity: planMetrics.monthlyActivity,
      categoryBreakdown: planMetrics.categoryBreakdown,
      account: {
        userId,
        status: accountSnapshot.status,
        roles: accountSnapshot.roles,
        plan: accountSnapshot.plan,
        quota: accountSnapshot.quota,
        subscriptionStatus: accountSnapshot.subscriptionStatus,
        createdAt: accountSnapshot.createdAt,
        lastActive: accountSnapshot.lastActive,
        lastLoginAt: accountSnapshot.lastLoginAt,
        suspendedUntil: accountSnapshot.suspendedUntil,
        suspensionReason: accountSnapshot.suspensionReason,
        planCount: accountSnapshot.planCount,
        shareCount: accountSnapshot.shareCount,
        flagsCount: accountSnapshot.flagsCount,
        achievementsCount: accountSnapshot.achievementsCount
      }
    }
  }

  async updateProfile(userId: string, payload: Record<string, unknown>): Promise<void> {
    logger.info('Updating user profile', { userId })
    const parsed = profileUpdateSchema.partial().parse(payload)
    const userRef = this.db.collection('users').doc(userId)

    logger.debug('Building profile updates', { userId, updateFields: Object.keys(parsed).length })
    const updates: Record<string, unknown> = {
      updatedAt: timestamp.now()
    }

    if (parsed.displayName) {
      const sanitized = this.sanitizeDisplayName(parsed.displayName)
      updates.displayName = sanitized
      await auth.updateUser(userId, { displayName: sanitized })
    }

    if (parsed.bio !== undefined) {
      updates.bio = parsed.bio.trim()
    }

    if (parsed.location !== undefined) {
      updates.location = parsed.location ? parsed.location.trim() : null
    }

    if (parsed.languages) {
      updates.languages = this.sanitizeLanguages(parsed.languages)
    }

    if (parsed.interests) {
      updates.interests = this.sanitizeInterests(parsed.interests)
    }

    if (parsed.avatarUrl !== undefined) {
      const avatar = parsed.avatarUrl ?? null
      updates.photoURL = avatar
      updates.avatarUrl = avatar
      await auth.updateUser(userId, { photoURL: avatar ?? undefined })
    }

    await userRef.set(updates, { merge: true })
    this.invalidateUserCaches(userId)
    logger.info('User profile updated successfully', { userId, updateFields: Object.keys(parsed).length })
  }

  async updatePreferences(userId: string, payload: PreferenceUpdates): Promise<UserPreferences> {
    logger.info('Updating user preferences', { userId, preferenceSections: Object.keys(payload).length })
    const parsed = preferencesUpdateSchema.parse(payload)
    const userRef = this.db.collection('users').doc(userId)

    return this.db.runTransaction(async (transaction) => {
      const userDoc = await transaction.get(userRef)
      if (!userDoc.exists) {
        throw new NotFoundError('User not found', { userId })
      }

      const current = this.normalizePreferences(userId, userDoc.data()?.preferences)

      logger.debug('Merging preference updates', { userId, sections: Object.keys(parsed) })
      const merged: UserPreferences = {
        ...current,
        travel: {
          ...current.travel,
          ...(parsed.travel)
        },
        display: {
          ...current.display,
          ...(parsed.display),
          accentColor: parsed.display?.accentColor as ProfileIconColor | undefined
        },
        notifications: {
          ...current.notifications,
          ...(parsed.notifications),
          types: {
            ...current.notifications.types,
            ...(parsed.notifications?.types)
          }
        },
        privacy: {
          ...current.privacy,
          ...(parsed.privacy)
        },
        security: {
          ...current.security,
          ...(parsed.security)
        },
        updatedAt: new Date()
      }

      transaction.set(
        userRef,
        {
          preferences: this.buildPreferencePayload(merged)
        },
        { merge: true }
      )

      return merged
    })
  }

  async recordInteraction(userId: string, payload: Record<string, unknown>): Promise<void> {
    logger.debug('Recording preference interaction', { userId, interactionType: (payload as Record<string, any>).type })
    const parsed = preferenceInteractionSchema.parse(payload)
    const userRef = this.db.collection('users').doc(userId)
    await userRef.collection('preferenceInteractions').add({
      ...parsed,
      timestamp: parsed.timestamp ? timestamp.fromDate(parsed.timestamp) : timestamp.now()
    })
    this.behaviorCache.delete(userId)
    logger.info('Preference interaction recorded', { userId, interactionType: parsed.type })
  }

  async getCategoryRecommendations(userId: string): Promise<CategoryRecommendation[]> {
    const analysis = await this.analyzeUserBehavior(userId)
    const weights = new Map<string, number>()

    const boostCategory = (category: string, weight: number) => {
      if (!category) return
      weights.set(category, (weights.get(category) ?? 1) * weight)
    }

    analysis.topCategories.forEach(category => boostCategory(category, 1.4))

    for (const season of Object.values(analysis.seasonalPreferences)) {
      season.forEach(category => boostCategory(category, 1.1))
    }

    if (analysis.budgetRange === 'low') {
      boostCategory('budget', 1.2)
    } else if (analysis.budgetRange === 'high') {
      boostCategory('premium', 1.2)
    }

    if (weights.size === 0) {
      return [
        { category: 'nature', weight: 1 },
        { category: 'culture', weight: 1 },
        { category: 'gastronomy', weight: 1 }
      ]
    }

    return Array.from(weights.entries())
      .map(([category, weight]) => ({ category, weight }))
      .sort((a, b) => b.weight - a.weight)
  }

  async revokeSession(userId: string, sessionId: string): Promise<void> {
    const sessionRef = this.db.collection('users').doc(userId).collection('sessions').doc(sessionId)
    await sessionRef.set(
      {
        current: false,
        revokedAt: timestamp.now()
      },
      { merge: true }
    )
    this.invalidateSessionCaches(userId)
  }

  async requestDataExport(userId: string): Promise<void> {
    const exportRef = this.db.collection('userExports').doc()
    await exportRef.set({
      userId,
      status: 'pending',
      createdAt: timestamp.now(),
      updatedAt: timestamp.now()
    })
  }

  async updateProfileIcon(userId: string, iconId: string, color?: string | null): Promise<User> {
    const userRef = this.db.collection('users').doc(userId)

    const iconSetUpdates: Record<string, unknown> = {
      activeIcon: iconId
    }
    const accentColor = this.normalizeIconColor(color)
    if (accentColor) {
      iconSetUpdates.accentColor = accentColor
    }

    const updates: Record<string, unknown> = {
      iconSet: iconSetUpdates,
      updatedAt: timestamp.now()
    }

    await userRef.set(updates, { merge: true })
    this.invalidateUserCaches(userId)

    const updatedDoc = await userRef.get()
    const userData = updatedDoc.data()

    if (!userData) {
      throw new NotFoundError('Failed to retrieve updated user', { userId })
    }

    const adminDetail = await this.adminRepository.getUserById(userId)
    return this.buildAccountSnapshot(userId, userData as Record<string, unknown>, adminDetail)
  }
}
